import {InjectionToken} from '@angular/core';
import {ComponentType} from '@angular/cdk/portal';
import {
    ArtifactId, AuditActivityId,
    AuditId,
    ConsoleSessionId,
    DeviceId,
    ExpeditionId,
    FindingId,
    LocalScreenshotId,
    ProjectId,
    ScreenshotId,
    TestObjectId,
    TestRunId,
    VideoRecordingId,
} from '@webmate-web-core/shared/types/ids';
import {Observable} from 'rxjs';
import {BrowserSessionInfo, InteractionPath} from '@webmate-web-core/shared/types/browsersession';
import {User} from '@webmate-web-core/shared/types/user/user';
import {AuditActivity} from '@webmate-web-core/shared/services/audit.service';
import {WorkbenchCommandConfig} from './services/workbench-command-registry.service';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {JSONValue} from '@webmate-web-core/shared/types/json';
import {ViewContainerId} from './components/viewcontainers/viewcontainers';
import {Params} from '@angular/router';
import {FindingCreationData} from '@webmate-web-core/shared/types/testmgmt';
import {WorkbenchLayoutManager} from './services/workbench-layout-manager.service';
import {ImpairedVisionSimulationConfig} from '../workbench-plugins/a11y-tools/views/impaired-vision-simulation-control-view.component';
import {WorkbenchMode} from '@webmate-web-core/shared/workbench-ipc';

export interface BrowserExpeditionStartConfig {
    browserType: string;
    browserVersion: string;
    startUrl?: string;
    interactionPaths?: InteractionPath[];
}

export interface OpenTestObjectInExpeditionConfig {
    testObjectId: TestObjectId;
}

export interface WorkbenchInitConfig {
    projectId: ProjectId;
    auditId?: AuditId;
    deviceId?: DeviceId;
    isChildWorkbench?: boolean;
    legacyProfile?: string;
    popoutConsoleId?: string;
    activeTestRun?: TestRunId;
    autoStartManualTest?: boolean;
    suppressAutoResumeTestRun?: boolean;
    browserExpeditionStartConfig?: BrowserExpeditionStartConfig;
    openTestObjectInExpeditionConfig?: OpenTestObjectInExpeditionConfig;
}

export interface WorkbenchPopoutConfig {
    projectId: ProjectId;
    consoleIdentifier: string;
    deviceId?: DeviceId;
}

export type WorkbenchActiveMode =
    {kind: 'default'}
    | {
        kind: 'manual-test';
        payload?: {
            autoStart?: boolean;
            suppressAutoResume?: boolean;
        };
    }
    | {
        kind: 'audit';
        payload: {
            auditId?: AuditId;
        };
    };

export type WorkbenchActiveModePayload =
    {autoStart?: boolean; suppressAutoResume?: boolean}
    | {auditId?: AuditId}
    | undefined;

export function workbenchModeFromActiveMode(activeMode: WorkbenchActiveMode): WorkbenchMode {
    return activeMode.kind;
}

export function makeQueryParamsFromInitConfig(config: WorkbenchInitConfig): Params {
    const params: Params = {
        projectId: config.projectId,
    };
    if (config.auditId) {
        params['auditId'] = config.auditId;
    }
    if (config.deviceId) {
        params['deviceId'] = config.deviceId;
    }
    if (config.popoutConsoleId) {
        params['popoutConsoleId'] = config.popoutConsoleId;
    }
    if (config.activeTestRun) {
        params['activeTestRun'] = config.activeTestRun;
    }
    if (config.autoStartManualTest) {
        params['autoStartManualTest'] = true;
    }
    if (config.suppressAutoResumeTestRun) {
        params['suppressAutoResumeTestRun'] = true;
    }
    if (config.browserExpeditionStartConfig) {
        params['browserExpeditionStartConfig'] = JSON.stringify(config.browserExpeditionStartConfig);
    }
    if (config.openTestObjectInExpeditionConfig) {
        params['openTestObjectInExpeditionConfig'] = JSON.stringify(config.openTestObjectInExpeditionConfig);
    }
    return params;
}

export function makeQueryParamsFromPopoutConfig(config: WorkbenchPopoutConfig): Params {
    const params: Params = {
        projectId: config.projectId,
        childWorkbench: true,
        popoutConsoleId: config.consoleIdentifier
    };
    if (config.deviceId) {
        params['deviceId'] = config.deviceId;
    }
    return params;
}

export type ViewCallback = Function | WorkbenchCommandHandler

export type ViewConfigPayload = {
    callbacks?: {[callbackName: string]: ViewCallback};
    data?: any;
}

export type ViewStyleConfig = {
    scrollable?: boolean; // default: true
    noMargin?: boolean; // default: false
}

export type ViewTemplateInitPayload = JSONValue;

export interface WorkbenchViewTemplateConfig<T extends ViewConfigPayload> extends Partial<WorkbenchViewConfig<T>> {
    templateId: ViewTemplateId;
    init: (d: ViewTemplateInitPayload) => WorkbenchViewConfig<T>;
}

export interface WorkbenchViewConfig<T extends ViewConfigPayload> {
    id: ViewId;
    name: string;
    icon?: IconSpec;
    pluginId?: WorkbenchPluginId;
    visibility?: WorkbenchViewVisibilityKind;

    component: ComponentType<any>;

    fromTemplate?: ViewTemplateId;
    restoredFromPersistence?: boolean;
    defaultViewContainer?: ViewContainerId;
    expandedByDefault?: boolean;
    payload?: T;
    styleConfig?: {
        tabbed?: ViewStyleConfig;
        stacked?: ViewStyleConfig;
    };
    popoutConfig?: WorkbenchPopoutConfig;
    lastActive?: number
}

export type WorkbenchViewKind = WorkbenchViewConfig<any> | WorkbenchViewTemplateConfig<any>

export const WORKBENCH_INIT_CONFIG = new InjectionToken<WorkbenchInitConfig | undefined>('workbench.init');

export const WORKBENCH_VIEW_CONFIG = new InjectionToken<WorkbenchViewConfig<any>>('WORKBENCH_VIEW_CONFIG');

export type ToolbarId = string;
export type ToolbarContainerId = string;
export type ToolbarConfig = CommandId[];

export interface ToolbarContainerState {
    id: ToolbarContainerId;
    config?: ToolbarConfig;
}

export type PanelId = 'left-top' | 'left-bottom' | 'bottom' | 'right' | 'center-center';

export type ViewId = string;

export type ViewTemplateId = string;


export type WorkbenchPluginId = 'a11y-tools' | 'audit' | 'console' | 'device' | 'expedition' | 'testmgmt'
                                | 'workbench-tools' | 'inspector' | 'working-memory'
                                | 'screenshot' | 'video-recording' | 'media-browser' | 'mail' | 'quality-sense';

export type WorkbenchViewVisibilityKind = 'generic' | 'manual-test' | 'audit';

export type WorkbenchDirection = 'left' | 'right' | 'up' | 'down' | 'newWindow';


export type IconSpec = {
    type: 'svg',
    path: string
} | {
    type: 'faIcon',
    instance: any
};

// export interface WorkbenchViewContainerComponent<T> extends ComponentType<T> {}

export interface View {
    id: ViewId;
}

export abstract class WorkbenchPlugin {
    abstract id: WorkbenchPluginId;
    abstract views: WorkbenchViewKind[];

    protected constructor(protected readonly layoutManager: WorkbenchLayoutManager) {}

    public registerViews(): void {
        for (const view of this.views.map(view => ({...view, pluginId: this.id}))) {
            if (this.isViewTemplate(view)) {
                if (!this.layoutManager.registeredViewTemplates[view.templateId]) {
                    this.layoutManager.registerViewTemplate(view);
                }
            } else {
                if (!this.layoutManager.registeredViews[view.id]) {
                    this.layoutManager.registerView(view);
                }
            }
        }
    }
    public abstract onWorkbenchInit(): Observable<void>;
    protected abstract onWorkbenchDestroy(): void;

    private isViewTemplate = (viewKind: WorkbenchViewKind): viewKind is WorkbenchViewTemplateConfig<any> => {
        return "templateId" in viewKind;
    }
}

export type CommandId = string;
export interface ConsoleInfo {
    consoleSession?: ConsoleSessionId;
    consoleViewId?: ViewId;
}

export type WorkbenchRef = string

/**
 * The workbench context is the "ambient" state of the workbench. It may be used by views, commands, and services.
 */
export interface WorkbenchContext {
    project: ProjectId;
    currentUser: User;
    activeMode: WorkbenchActiveMode;
    auditContext?: AuditContext;
    deviceContext?: DeviceContext;
    expeditionContext?: ExpeditionContext;
    testRunContext?: TestRunContext;

    // a "pseudo finding" is a finding that is not yet created in the backend,
    // but is used to collect cells before the user decides to create it.
    pseudoFinding?: FindingCreationData;

    parentWorkBench?: WorkbenchRef;
    isChildWorkbench: boolean;
    childWorkbenches?: WorkbenchRef[];
}

export interface DeviceContext {
    activeDevice?: DeviceId;
    activeConsole?: ConsoleInfo;
    openedConsoles?: ConsoleInfo[];
}

export interface AuditContext {
    activeAudit?: AuditId;
    activeAuditActivity?: AuditActivity;
    activeFinding?: FindingId;
}

export interface TestRunContext {
    activeTestRun?: TestRunId;
}

export interface ExpeditionContext {
    activeExpedition?: BrowserSessionInfo;
    activeTestRun?: TestRunId;
    // In-memory lock: when true, the active browser session is held fixed and does NOT follow the active
    // console, and session-changing UIs are disabled. Held by a use case (e.g. a running analysis) for its
    // duration and released when it finishes — there is no manual unlock. Reference-counted in the service.
    // Intentionally not persisted, so it resets on reload.
    activeExpeditionLocked?: boolean;
}

export type StoreKey = string;

export interface WorkbenchCommand {
    id: CommandId;
    config: WorkbenchCommandConfig;
}

export interface WorkbenchCommandHandler {
    handleCommand(id: CommandId, params: any): Observable<any> | void;
}

// Inspector
export type InspectableItemType = 'Audit' | 'Screenshot' | 'VideoRecording' | 'TestMail'| 'DeviceDetails' | 'Default';

export type InspectorEventType = 'OpenScreenshot' | 'SwitchToFindingTab' | 'OpenAuditActivity' | 'ClearInspector' | 'OpenTestMail' | 'OpenVideoRecording' | 'OpenDeviceDetails' | 'NameChanged' | 'OpenFindingForActivityEvent' | 'EditingScreenshot';

export interface InspectorEvent {
    type: InspectorEventType;
}

export interface OpenScreenshotEvent extends InspectorEvent {
    type: 'OpenScreenshot',
    localId: LocalScreenshotId,
    id: ScreenshotId,
    inEditMode: boolean,
}

export interface OpenTestMailEvent extends InspectorEvent {
    type: 'OpenTestMail',
    mailArtifatId: ArtifactId
}

export interface OpenVideoRecordingEvent extends InspectorEvent {
    type: 'OpenVideoRecording',
    id: VideoRecordingId,
    name: string,
    videoUrl: string;
    duration?: number,
    size?: number,
    format?: string,
}

export interface SwitchToFindingTabsEvent extends InspectorEvent {
    type: 'SwitchToFindingTab'
}

export interface OpenAuditActivityEvent extends InspectorEvent {
    type: 'OpenAuditActivity',
    activityID: string,
}

export interface OpenFindingForActivityEvent extends InspectorEvent {
    type: 'OpenFindingForActivityEvent',
    activityId: AuditActivityId,
    findingId: FindingId
}

export interface OpenDeviceDetailsEvent extends InspectorEvent {
    type: 'OpenDeviceDetails',
    targetDevice: DeviceId
}

export interface EditingScreenshotEvent extends InspectorEvent {
    type: 'EditingScreenshot',
    editing: boolean
}

export interface InspectorImplementation {
    handleInspectorEvent(event: InspectorEvent): void;
}

export interface WorkbenchTool {
    id: string;
    label: string;
    command: string;
    icon: IconDefinition;
}
